import React from 'react';
import DocLayout from '../DocLayout';
import { CheckCircle2, Mail, User } from 'lucide-react';

const CreateAccount = () => {
  return (
    <DocLayout title="How to Create Your Account" category="Getting Started">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-6 h-6 text-indigo-600" />
            Creating Your I-Match Account
          </h2>
          <p className="text-slate-700 mb-4">
            Getting started with I-Match is quick and easy. Follow these steps to create your account and begin transforming your lead management process.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Step 1: Choose Your Plan</h3>
          <p className="text-slate-700 mb-3">
            Navigate to the I-Match landing page and click on "Enroll Now" under your preferred plan:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Base Plan</strong> (€29/month): 5 users, 500 leads</li>
            <li><strong>Tier 1</strong> (€59/month): 25 users, 2500 leads, AI intro mail</li>
            <li><strong>Tier 2</strong> (€89/month): 50 users, 2500 leads, AI intro mail</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Step 2: Company Information</h3>
          <p className="text-slate-700 mb-3">
            Fill in your company details:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li>Company Name (must be unique)</li>
            <li>Company Email</li>
            <li>Phone Number</li>
            <li>Website URL</li>
            <li>Company Description</li>
            <li>Industry</li>
            <li>Company Size</li>
            <li>Location</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Step 3: Add Team Members</h3>
          <p className="text-slate-700 mb-3">
            Set up your team during registration:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li>Add at least one admin user (yourself)</li>
            <li>Add at least one normal user</li>
            <li>You can add unlimited users during this step</li>
            <li>Each user will receive an email verification link</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Step 4: Email Verification</h3>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-yellow-600 mt-1" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">Important: Email Verification Required</p>
                <p className="text-slate-700">
                  All users must verify their email before they can log in. Check your inbox for a verification email and click the verification link.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Step 5: First Login</h3>
          <p className="text-slate-700 mb-3">
            After email verification:
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-slate-700">
            <li>Visit the login page</li>
            <li>Enter your email and password</li>
            <li>You'll be taken to your personalized dashboard</li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Next Steps</h3>
          <div className="bg-blue-50 rounded-lg p-4">
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-slate-700">Set up your workspace and company profile</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-slate-700">Import your first leads</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-slate-700">Explore AI enrichment features</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default CreateAccount;